import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/layout/SiteHeader";
import { TrustChip } from "@/components/shared";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "colors", label: "Colors" },
  { key: "typography", label: "Typography" },
  { key: "components", label: "Components" },
  { key: "messaging", label: "Messaging" },
  { key: "homepage-rules", label: "Homepage Rules" },
];

const COLORS = [
  ["Dark Blue", "#143257", "Structural anchor — headers, footers, major text"],
  ["Primary Red", "#C62828", "Kinetic trigger — donate, urgent CTAs, alerts"],
  ["White", "#FFFFFF", "Base background"],
  ["Light Gray", "#F3F4F6", "Alternate surface"],
  ["Medium Gray", "#9CA3AF", "Muted text, borders"],
  ["Dark Gray", "#374151", "Body text"],
];

export default function BrandGuidelines() {
  const { section = "overview" } = useParams();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-ff-border bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
          <Logo />
          <span className="rounded-full bg-[#F3F4F6] px-3 py-1 text-xs font-bold uppercase tracking-wider text-ff-blue">Brand Guidelines</span>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-10 sm:px-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <nav className="sticky top-6 flex flex-row flex-wrap gap-1 lg:flex-col">
            {SECTIONS.map((s) => (
              <button key={s.key} data-testid={`brand-nav-${s.key}`} onClick={() => navigate(`/brand-guidelines/${s.key}`)}
                className={`rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors duration-200 ${section === s.key ? "bg-ff-blue text-white" : "text-ff-dark hover:bg-[#F3F4F6]"}`}>
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="lg:col-span-9">
          {section === "overview" && (
            <Section title="FightFund Brand" data-testid="brand-overview">
              <p className="text-ff-dark">Patriotic but not cheesy. Modern. Trustworthy. Not political, not theatrical, not gimmicky.</p>
              <div className="mt-6 rounded-2xl border border-ff-border bg-ff-blue p-8 text-white">
                <div className="font-heading text-2xl font-extrabold">Fast for donors. Strict for fundraisers. Controlled before payout.</div>
                <p className="mt-3 text-white/70">The core message anchors everything we build.</p>
              </div>
            </Section>
          )}

          {section === "colors" && (
            <Section title="Colors" data-testid="brand-colors">
              <div className="grid gap-4 sm:grid-cols-2">
                {COLORS.map(([name, hex, use]) => (
                  <div key={hex} className="overflow-hidden rounded-xl border border-ff-border">
                    <div className="h-20" style={{ backgroundColor: hex }} />
                    <div className="p-4">
                      <div className="font-heading font-bold text-ff-blue">{name}</div>
                      <div className="text-sm font-mono text-ff-dark">{hex}</div>
                      <div className="mt-1 text-xs text-ff-medium">{use}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {section === "typography" && (
            <Section title="Typography" data-testid="brand-typography">
              <div className="space-y-6">
                <div className="rounded-xl border border-ff-border p-6">
                  <div className="text-xs font-bold uppercase tracking-wider text-ff-medium">Headings — Manrope</div>
                  <div className="mt-2 font-heading text-4xl font-extrabold text-ff-blue">Manrope Extrabold</div>
                  <div className="font-heading text-2xl font-bold text-ff-blue">Manrope Bold</div>
                </div>
                <div className="rounded-xl border border-ff-border p-6">
                  <div className="text-xs font-bold uppercase tracking-wider text-ff-medium">Body / UI — Inter</div>
                  <p className="mt-2 text-base text-ff-dark">Inter is used for all body text and UI. It's highly legible and neutral, keeping the focus on trust and clarity.</p>
                </div>
              </div>
            </Section>
          )}

          {section === "components" && (
            <Section title="Components" data-testid="brand-components">
              <div className="space-y-6">
                <Block label="Buttons">
                  <div className="flex flex-wrap gap-3">
                    <Button className="bg-ff-action text-white hover:bg-ff-actionDark">Primary (Donate / Start)</Button>
                    <Button className="bg-ff-blue text-white hover:bg-ff-blue/90">Secondary</Button>
                    <Button variant="outline" className="border-ff-blue text-ff-blue">Outline</Button>
                  </div>
                </Block>
                <Block label="Trust chips">
                  <div className="flex flex-wrap gap-2">
                    {["Campaign Reviewed", "Organizer Verified", "Nonprofit Verified", "Payout Under Review", "Funds Released"].map((l) => <TrustChip key={l} label={l} />)}
                  </div>
                </Block>
              </div>
            </Section>
          )}

          {section === "messaging" && (
            <Section title="Messaging" data-testid="brand-messaging">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-ff-green/30 bg-[#ECFDF3] p-5">
                  <div className="font-heading font-bold text-ff-green">Say this</div>
                  <ul className="mt-2 space-y-1.5 text-sm text-ff-dark">
                    <li>FightFund reviews campaigns and payout requests before funds are released.</li>
                    <li>Start a fundraiser for free.</li>
                    <li>Donate in seconds.</li>
                    <li>Built for U.S. fundraisers, donors, and nonprofits.</li>
                  </ul>
                </div>
                <div className="rounded-xl border border-ff-red/30 bg-[#FEF2F2] p-5">
                  <div className="font-heading font-bold text-ff-red">Never say this</div>
                  <ul className="mt-2 space-y-1.5 text-sm text-ff-dark">
                    <li>“Guaranteed safe”</li>
                    <li>“Fraud-proof”</li>
                    <li>“Fully verified in every case”</li>
                  </ul>
                </div>
              </div>
            </Section>
          )}

          {section === "homepage-rules" && (
            <Section title="Homepage Rules" data-testid="brand-homepage-rules">
              <ul className="space-y-3 text-ff-dark">
                <li>• Lead with the core message and a clear primary CTA (Start a Fundraiser).</li>
                <li>• Show honest trust labels in the hero; never overpromise safety.</li>
                <li>• Use Dark Blue for structure, Primary Red only for the primary action.</li>
                <li>• Keep it modern and patriotic without being theatrical or political.</li>
                <li>• Feature reviewed, live campaigns to build trust.</li>
              </ul>
            </Section>
          )}

          <div className="mt-10 border-t border-ff-border pt-6">
            <Button asChild variant="ghost" className="text-ff-red"><Link to="/">← Back to FightFund</Link></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, ...props }) {
  return (
    <div {...props}>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-ff-blue">{title}</h1>
      <div className="mt-6">{children}</div>
    </div>
  );
}
function Block({ label, children }) {
  return (
    <div className="rounded-xl border border-ff-border p-6">
      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-ff-medium">{label}</div>
      {children}
    </div>
  );
}
